import { CloseOutlined } from "@ant-design/icons";
import { Button, Flex, Tree, Typography } from "antd";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import {
  getPropNodePorts,
  type PropGraphPort,
  type PropNodeType,
} from "../../shared/types/PropGraph";
import {
  getPropGraphNodeDocumentTitle,
  getPropGraphNodeParameterDocs,
  getPropGraphNodeRuntimeDetail,
  getPropGraphNodeUsageDoc,
  propGraphNodeDocumentSummaries,
} from "./nodeDocs";
import { PropGraphNodeDesignDocument, PropGraphOverviewDocument } from "./overviewDocs";
import {
  getPropGraphPortDocumentDescription,
  getPropGraphPortDocumentLabel,
  getPropGraphPortKindDocumentLabel,
  getPropGraphPortRequiredText,
  getPropGraphValueTypeDocumentLabel,
  PropGraphPortDocument,
} from "./portDocs";
import {
  PropGraphDocsArticle,
  PropGraphDocsContent,
  PropGraphDocsLayout,
  PropGraphDocsSidebar,
  PropGraphDocsText,
} from "./styles";
import { createPropGraphDocumentTreeDataWithNodePages } from "./tree";
import type { PortDirection, PropGraphNodeExampleDoc } from "./types";

const { Text } = Typography;

const PROP_GRAPH_DOCUMENT_DEFAULT_KEY = "overview";

export function PropGraphDocsTab({ onClose }: { onClose: () => void }) {
  const [selectedKey, setSelectedKey] = useState(PROP_GRAPH_DOCUMENT_DEFAULT_KEY);
  const treeData = useMemo(createPropGraphDocumentTreeDataWithNodePages, []);

  return (
    <PropGraphDocsLayout>
      <PropGraphDocsSidebar>
        <Flex align="center" justify="space-between" style={{ marginBottom: 10 }}>
          <Text strong>文档目录</Text>
          <Button size="small" icon={<CloseOutlined />} onClick={onClose} />
        </Flex>
        <Tree
          blockNode
          defaultExpandAll
          selectedKeys={[selectedKey]}
          treeData={treeData}
          onSelect={(keys) => {
            const nextKey = String(keys[0] ?? selectedKey);
            if (nextKey) setSelectedKey(nextKey);
          }}
        />
      </PropGraphDocsSidebar>
      <PropGraphDocsContent>
        {renderPropGraphDocument(selectedKey)}
      </PropGraphDocsContent>
    </PropGraphDocsLayout>
  );
}

function renderPropGraphDocument(key: string) {
  if (key.startsWith("node:")) {
    return <PropGraphNodeDetailDocument type={key.slice("node:".length) as PropNodeType} />;
  }
  if (key.startsWith("nodes")) return <PropGraphNodeDesignDocument activeKey={key} />;
  if (key.startsWith("ports")) return <PropGraphPortDocument activeKey={key} />;
  return <PropGraphOverviewDocument activeKey={key} />;
}

function PropGraphNodeDetailDocument({ type }: { type: PropNodeType }) {
  const ports = getPropNodePorts(type);
  const parameterDocs = getPropGraphNodeParameterDocs(type);
  const usageDoc = getPropGraphNodeUsageDoc(type);

  return (
    <PropGraphDocsArticle>
      <h2>{getPropGraphNodeDocumentTitle(type)}</h2>
      <h3>节点摘要</h3>
      <DocText text={propGraphNodeDocumentSummaries[type]} />
      <h3>节点用途</h3>
      <DocText text={usageDoc.purpose} />
      <h3>适用场景</h3>
      <ul>
        {usageDoc.scenes.map((scene, index) => (
          <li key={index}><DocText text={scene} /></li>
        ))}
      </ul>
      <h3>运行语义</h3>
      <DocText text={getPropGraphNodeRuntimeDetail(type)} />
      <PropGraphNodePortTable title="输入端口" direction="input" ports={ports.inputs} type={type} />
      <PropGraphNodePortTable title="输出端口" direction="output" ports={ports.outputs} type={type} />
      <h3>参数</h3>
      {parameterDocs.length ? (
        <table>
          <thead>
            <tr>
              <th>参数</th>
              <th>字段</th>
              <th>是否必填</th>
              <th>说明</th>
            </tr>
          </thead>
          <tbody>
            {parameterDocs.map((item) => (
              <tr key={item.field}>
                <td>{item.name}</td>
                <td><code>{item.field}</code></td>
                <td>{item.required}</td>
                <td><DocText text={item.description} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>无参数。</p>
      )}
      <PropGraphNodeExampleTable examples={usageDoc.examples} />
      <h3>常见错误</h3>
      <ul>
        {usageDoc.pitfalls.map((pitfall, index) => (
          <li key={index}><DocText text={pitfall} /></li>
        ))}
      </ul>
      <h3>游戏端实现思路</h3>
      <pre>{formatPropGraphNodeRuntimePseudoCode(type)}</pre>
    </PropGraphDocsArticle>
  );
}

function PropGraphNodeExampleTable({ examples }: { examples: PropGraphNodeExampleDoc[] }) {
  return (
    <>
      <h3>典型示例</h3>
      <table>
        <thead>
          <tr>
            <th>示例</th>
            <th>节点组合</th>
            <th>效果</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          {examples.map((example) => (
            <tr key={example.title}>
              <td>{example.title}</td>
              <td><DocText text={example.graph} /></td>
              <td><DocText text={example.result} /></td>
              <td><DocText text={example.note ?? "用于说明该节点在真实道具图中的常见组合方式。"} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function PropGraphNodePortTable({
  title,
  direction,
  ports,
  type,
}: {
  title: string;
  direction: PortDirection;
  ports: PropGraphPort[];
  type: PropNodeType;
}) {
  if (!ports.length) return null;
  return (
    <>
      <h3>{title}</h3>
      <table>
        <thead>
          <tr>
            <th>端口</th>
            <th>端口 ID</th>
            <th>类型</th>
            <th>数据类型</th>
            <th>{direction === "output" ? "输出形式" : "是否必填"}</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          {ports.map((port) => (
            <tr key={`${direction}:${port.id}`}>
              <td>{getPropGraphPortDocumentLabel(port)}</td>
              <td><code>{port.id}</code></td>
              <td>{getPropGraphPortKindDocumentLabel(port.kind)}</td>
              <td>{getPropGraphValueTypeDocumentLabel(port.valueType)}</td>
              <td>{direction === "output" ? "函数" : getPropGraphPortRequiredText(port)}</td>
              <td><DocText text={getPropGraphPortDocumentDescription(type, port, direction)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DocText({ text, compact }: { text: ReactNode; compact?: boolean }) {
  if (typeof text !== "string" && typeof text !== "number") {
    return <PropGraphDocsText $compact={compact}>{text}</PropGraphDocsText>;
  }

  const lines = splitDescriptionLines(String(text));
  return (
    <PropGraphDocsText $compact={compact}>
      {lines.map((line, index) => (
        <span className="propgraph-description-line" key={`${index}:${line}`}>
          {line}
        </span>
      ))}
    </PropGraphDocsText>
  );
}

function splitDescriptionLines(text: string) {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return [""];

  const primaryParts = normalized.match(/[^。；;]+[。；;]?/g) ?? [normalized];
  return primaryParts.flatMap((part) => splitLongDescriptionPart(part.trim())).filter(Boolean);
}

function splitLongDescriptionPart(text: string) {
  if (text.length <= 44 || !text.includes("，")) return [text];
  const commaParts = text.match(/[^，,]+[，,]?/g) ?? [text];
  const lines: string[] = [];
  let current = "";

  commaParts.forEach((part) => {
    const next = `${current}${part}`;
    if (current && next.length > 44) {
      lines.push(current);
      current = part;
      return;
    }
    current = next;
  });

  if (current) lines.push(current);
  return lines;
}

function formatPropGraphNodeRuntimePseudoCode(type: PropNodeType) {
  const code = propGraphNodeRuntimePseudoCode[type];
  if (code) return code;
  return [
    "function runNode(context) {",
    "  const inputs = evaluateInputs(context);",
    "  const result = evaluateByNodeType(inputs, context);",
    "  return result;",
    "}",
  ].join("\n");
}

const propGraphNodeRuntimePseudoCode: Record<PropNodeType, string> = {
  eventTurnStart: [
    "function onTurnStart(instance, node, context) {",
    "  const interval = max(0, floor(node.data.interval ?? 0));",
    "  const period = interval + 1;",
    "  const loop = max(0, floor(node.data.loop ?? 0));",
    "  const triggered = instance.nodeState[node.id]?.triggered ?? 0;",
    "  if (loop > 0 && triggered >= loop) return;",
    "  if (context.turnIndex % period !== 0) return;",
    "  instance.nodeState[node.id].triggered = triggered + 1;",
    "  output.flow?.({ ...context, owner: instance });",
    "}",
  ].join("\n"),
  eventAcquire: [
    "function onHeldTick(instance, node, context) {",
    "  if (!context.isHeld(instance)) return;",
    "  output.flow?.({ ...context, owner: instance });",
    "}",
    "",
    "function onRemoved(instance, node, context) {",
    "  context.removeEffectsCreatedBy(instance, node.id);",
    "}",
  ].join("\n"),
  eventUse: [
    "function onUse(instance, node, context) {",
    "  if (!context.canUse(instance)) return;",
    "  context.consumeUseIfNeeded(instance);",
    "  output.flow?.({ ...context, owner: instance });",
    "}",
  ].join("\n"),
  eventEliminated: [
    "function onTurnBegin(instance, node, context) {",
    "  const targets = input.target?.(context) ?? [];",
    "  targets.forEach((target) => {",
    "    context.listenEliminated(target.id, node.id, (event) => {",
    "      if (!event.removed) return;",
    "      output.flow?.({ ...context, owner: instance, eventTarget: target });",
    "    });",
    "  });",
    "}",
    "",
    "function onTurnEnd(instance, node, context) {",
    "  context.removeListenerByNode(node.id);",
    "}",
  ].join("\n"),

  targetSelf: [
    "function outputTarget(context) {",
    "  return context.owner ? [context.owner] : [];",
    "}",
  ].join("\n"),
  targetBoard: [
    "function outputTargets(context, node) {",
    "  const all = context.board.getMaterials();",
    "  if (node.data.scope === 'all') return all;",
    "  if (node.data.scope === 'relative') {",
    "    const anchors = context.currentTargets?.length ? context.currentTargets : [context.owner];",
    "    return unique(anchors.flatMap((anchor) =>",
    "      context.board.findByRelativePositions(anchor, node.data.position ?? [])",
    "    ));",
    "  }",
    "  if (node.data.scope === 'absolute') {",
    "    return context.board.findByAbsolutePositions(node.data.position ?? []);",
    "  }",
    "  return [];",
    "}",
  ].join("\n"),
  prototypeLibrary: [
    "function outputPrototypes(context, node) {",
    "  const propType = node.data.propType ?? 1;",
    "  return context.propTable",
    "    .all()",
    "    .filter((prop) => prop.type === propType && !prop.derived);",
    "}",
  ].join("\n"),
  prototypeSelect: [
    "function outputPrototypes(context, node) {",
    "  const prototype = context.propTable.get(node.data.prototypeId);",
    "  return prototype ? [prototype] : [];",
    "}",
  ].join("\n"),

  targetFilter: [
    "function outputTargets(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  return targets.filter((target) => input.condition?.({ ...context, currentTarget: target }) === true);",
    "}",
  ].join("\n"),
  filterRarity: [
    "function outputTargets(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  const rarities = input.rarityList.map((read) => read(context));",
    "  return targets.filter((target) => rarities.includes(target.rarity));",
    "}",
  ].join("\n"),
  filterTag: [
    "function outputTargets(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  const tags = input.tagList.map((read) => read(context));",
    "  return targets.filter((target) => target.tags.some((tag) => tags.includes(tag)));",
    "}",
  ].join("\n"),
  filterPrototype: [
    "function outputTargets(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  const prototypes = input.prototypeList.flatMap((read) => read(context));",
    "  const prototypeIds = new Set(prototypes.map((prop) => prop._id));",
    "  return targets.filter((target) => prototypeIds.has(target.prototypeId));",
    "}",
  ].join("\n"),
  filterRandom: [
    "function outputTargets(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  const count = max(0, floor(input.count?.(context) ?? 0));",
    "  if (count >= targets.length) return targets;",
    "  return context.rng.pickMany(targets, count);",
    "}",
  ].join("\n"),

  actionGrow: [
    "function runGrow(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  const amount = floor(input.amount?.(context) ?? 0);",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    target.permanentYield += amount;",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionExtraYield: [
    "function runExtraYield(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [context.owner];",
    "  const amount = input.amount?.(context) ?? 0;",
    "  const multiplier = input.multiplier?.(context);",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    context.turnMana += amount;",
    "    if (multiplier !== undefined) context.turnMana += target.currentYield * multiplier;",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionPermanentBoost: [
    "function runPermanentBoost(context, node) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  const boost = {",
    "    sourceId: context.owner.id,",
    "    mutexTag: node.data.mutexTag ?? 'default',",
    "    amount: input.amount?.(context),",
    "    multiplier: input.multiplier?.(context),",
    "  };",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    target.removeBoostByMutex(boost.mutexTag);",
    "    target.addBoost(boost);",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionPurify: [
    "function runPurify(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = limitTargets(input.targets?.(context) ?? [], input.amount?.(context));",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    target.effects = target.effects.filter((effect) => !effect.negative);",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionProtect: [
    "function runProtect(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    target.protected = true;",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
    "",
    "function eliminateProtectedTarget(target) {",
    "  target.protected = false;",
    "  return { success: true, removed: false };",
    "}",
  ].join("\n"),
  actionAccelerate: [
    "function runAccelerate(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  const amount = floor(input.amount?.(context) ?? 0);",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    target.countdown += amount;",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionTransform: [
    "function runTransform(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  const prototypes = input.prototypes?.(context) ?? [];",
    "  if (!targets.length || !prototypes.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    const nextPrototype = context.rng.pickOne(prototypes);",
    "    context.board.replacePrototype(target, nextPrototype._id);",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionSpawn: [
    "function runSpawn(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const prototypes = input.targets?.(context) ?? [];",
    "  const positions = input.position?.(context) ?? [];",
    "  const count = max(0, floor(input.amount?.(context) ?? 1));",
    "  if (!prototypes.length || count <= 0) return output.fail?.(context);",
    "  repeat(count, () => {",
    "    const prototype = context.rng.pickOne(prototypes);",
    "    const position = context.board.pickSpawnPosition(positions);",
    "    context.spawnMaterial(prototype._id, position);",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionEliminate: [
    "function runEliminate(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = limitTargets(input.targets?.(context) ?? [], input.amount?.(context));",
    "  if (!targets.length) return output.fail?.(context);",
    "  let success = false;",
    "  targets.forEach((target) => {",
    "    const result = context.eliminate(target);",
    "    if (result.success) {",
    "      success = true;",
    "      output.each?.({ ...context, currentTarget: target, removed: result.removed });",
    "    }",
    "  });",
    "  return success ? output.complete?.(context) : output.fail?.(context);",
    "}",
  ].join("\n"),
  actionCollect: [
    "function runCollect(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  const amount = input.amount?.(context) ?? 0;",
    "  if (!targets.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    context.owner.storedMana += amount;",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionHarvest: [
    "function runHarvest(context) {",
    "  const stored = context.owner.storedMana ?? 0;",
    "  if (stored <= 0) return output.fail?.(context);",
    "  context.turnMana += stored;",
    "  context.owner.storedMana = 0;",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionRarityWeight: [
    "function runRarityWeight(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const rarity = input.rarity?.(context);",
    "  const multiplier = input.multiplier?.(context) ?? 1;",
    "  if (!rarity || multiplier < 0) return output.fail?.(context);",
    "  context.rarityWeights.multiply(rarity, multiplier, context.owner.id);",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionExtendCommission: [
    "function runExtendCommission(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  if (context.commission.hasExtendedBy(context.owner.prototypeId)) return output.fail?.(context);",
    "  context.commission.extraTurns += 1;",
    "  context.commission.markExtendedBy(context.owner.prototypeId);",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionSwapPosition: [
    "function runSwapPosition(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  const positions = input.positions?.(context) ?? [];",
    "  if (!targets.length || !positions.length) return output.fail?.(context);",
    "  targets.forEach((target) => {",
    "    const positionTarget = context.rng.pickOne(positions);",
    "    context.board.swap(target, positionTarget);",
    "    output.each?.({ ...context, currentTarget: target });",
    "  });",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),
  actionTriggerVisual: [
    "function runTriggerVisual(context) {",
    "  if (!allConditionsPass(context)) return output.fail?.(context);",
    "  const targets = input.targets?.(context) ?? [];",
    "  context.emitAnimationTriggered({",
    "    source: context.owner,",
    "    targets,",
    "    selfAnimations: node.data.selfAnimations ?? [],",
    "    targetAnimations: node.data.targetAnimations ?? [],",
    "    selfEffects: node.data.selfEffects ?? [],",
    "    targetEffects: node.data.targetEffects ?? [],",
    "  });",
    "  targets.forEach((target) => output.each?.({ ...context, currentTarget: target }));",
    "  output.complete?.(context);",
    "}",
  ].join("\n"),

  intConstant: [
    "function outputValue(context, node) {",
    "  return floor(node.data.value ?? 0);",
    "}",
  ].join("\n"),
  floatConstant: [
    "function outputValue(context, node) {",
    "  return Number.isFinite(node.data.value) ? node.data.value : 0;",
    "}",
  ].join("\n"),
  rarityConstant: [
    "function outputRarity(context, node) {",
    "  return context.rarities.has(node.data.value) ? node.data.value : undefined;",
    "}",
  ].join("\n"),
  tagConstant: [
    "function outputTag(context, node) {",
    "  return context.tags.has(node.data.value) ? node.data.value : undefined;",
    "}",
  ].join("\n"),
  intAttribute: [
    "function outputValue(context, node) {",
    "  const target = input.target?.(context)?.[0];",
    "  if (!target) return 0;",
    "  if (node.data.attribute === 'rarity') return target.rarity;",
    "  if (node.data.attribute === 'tag') return target.tags[0] ?? 0;",
    "  if (node.data.attribute === 'yield') return floor(target.currentYield);",
    "  return 0;",
    "}",
  ].join("\n"),
  floatAttribute: [
    "function outputValue(context, node) {",
    "  const target = input.target?.(context)?.[0];",
    "  if (!target) return 0;",
    "  return Number(readAttribute(target, node.data.attribute) ?? 0);",
    "}",
  ].join("\n"),
  intQuantity: [
    "function outputValue(context) {",
    "  return (input.targets?.(context) ?? []).length;",
    "}",
  ].join("\n"),
  intKindCount: [
    "function outputValue(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  return new Set(targets.map((target) => target.prototypeId)).size;",
    "}",
  ].join("\n"),
  intRandom: [
    "function outputValue(context) {",
    "  const min = floor(input.min?.(context) ?? 0);",
    "  const max = floor(input.max?.(context) ?? min);",
    "  return context.rng.intBetween(Math.min(min, max), Math.max(min, max));",
    "}",
  ].join("\n"),
  floatRandom: [
    "function outputValue(context) {",
    "  const min = input.min?.(context) ?? 0;",
    "  const max = input.max?.(context) ?? min;",
    "  return context.rng.floatBetween(Math.min(min, max), Math.max(min, max));",
    "}",
  ].join("\n"),
  intOperation: [
    "function outputValue(context, node) {",
    "  const left = floor(input.left?.(context) ?? 0);",
    "  const right = floor(input.right?.(context) ?? 0);",
    "  return floor(applyIntegerOperation(node.data.op, left, right));",
    "}",
  ].join("\n"),
  floatOperation: [
    "function outputValue(context, node) {",
    "  const left = input.left?.(context) ?? 0;",
    "  const right = input.right?.(context) ?? 0;",
    "  return applyFloatOperation(node.data.op, left, right);",
    "}",
  ].join("\n"),
  roundNumber: [
    "function outputValue(context, node) {",
    "  const value = input.value?.(context) ?? 0;",
    "  if (node.data.mode === 'ceil') return Math.ceil(value);",
    "  if (node.data.mode === 'floor') return Math.floor(value);",
    "  return Math.round(value);",
    "}",
  ].join("\n"),

  logicCompareInt: [
    "function outputCondition(context, node) {",
    "  const left = floor(input.left?.(context) ?? 0);",
    "  const right = floor(input.right?.(context) ?? 0);",
    "  return compare(node.data.op, left, right);",
    "}",
  ].join("\n"),
  logicCompareFloat: [
    "function outputCondition(context, node) {",
    "  const left = input.left?.(context) ?? 0;",
    "  const right = input.right?.(context) ?? 0;",
    "  return compare(node.data.op, left, right);",
    "}",
  ].join("\n"),
  logicContains: [
    "function outputCondition(context) {",
    "  const targets = input.targets?.(context) ?? [];",
    "  const targetIds = new Set(targets.map((target) => target.id));",
    "  return (input.target?.(context) ?? []).some((target) => targetIds.has(target.id));",
    "}",
  ].join("\n"),
  logicProbability: [
    "function outputCondition(context, node) {",
    "  const probability = clamp(node.data.probability ?? 0, 0, 1);",
    "  return context.rng.next() < probability;",
    "}",
  ].join("\n"),
};
